import React from 'react';

import {assert} from 'Asserts';
import {
  ProcedureType,
  ProcessExecutionType,
  ProcessFlowchartNodeDetailsType,
  ProcessSingletonTaskSchemaName,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import DocumentActions from 'notebook/documents/DocumentActions';
import DocumentStore from 'notebook/documents/DocumentStore';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  composeProcedureTypeMetadata,
  PROCEDURE_SAVE_ENDED_EVENT,
  PROCEDURE_SAVE_STARTED_EVENT,
} from 'performance/processProceduresEvents';
import mergeIdsIntoPowProcedureFlowchartConfig from 'procedures/network/mergeIdsIntoPowProcedureFlowchart';
import {ParameterUsageNodeGraphQL} from 'procedures/network/parseProcedureFromNetwork';
import {getDocumentVersionUpdatesFromFlowchartConfig} from 'procedures/procedurePagePersistenceUtils';
import {isStaticMethodNode} from 'procedures/processes/MethodNodeUtils';
import {TaskDefinitionVersionSpec} from 'processes/dao/ProcessFlowchartConfigDAO';
import {
  FlowchartConfigWithMetadataAndRootTaskSchema,
  ProcessFlowchartAdapter,
  ProcessFlowchartAdditionalPersistedData,
  RemoveNodeConfirmationContent,
  RemoveNodeConfirmationParams,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartAdapter';
import {
  FlowchartNodeEditMode,
  NodeCreationContext,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import {isTaskNodeDetailsGQL} from 'processes/graph-builder-processes/flowchart-viz/util/getNodeDetailsTypeForGQL';
import {clientOriginToProcedureType} from 'processes/ProcessConstants';
import {
  STANDARD_END_NODE_ICON,
  STANDARD_END_NODE_NAME,
  STANDARD_ROOT_NODE_ICON,
  STANDARD_ROOT_NODE_NAME,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {ProcessTaskDefinitionVersionGraphQL} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  TaskDefinitionVersionSpecWithTaskSchema,
  TaskSchemaNodeSectionData,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {ProcessFlowchartConfigForEditorWithLatestVersionGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessFlowchartConfigForEditorWithLatestVersionGraphQL';
import {ProcessTaskSchemaGraphQL} from '../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from '../ProceduresStringKeys';
import {StepIdToDocumentIdAndVersionIdMap, ValuableProcedureServerData} from './ProcedureFlowchartTypes';

/** Exported only for testing */
export function createIdForAmountField(materialInputVersionId: string): string {
  // The special index lets us store two fields for the same material input version
  return `${materialInputVersionId}-amt`;
}

/** @package */
export function extractValuableProcedureDataFromServerResponse(
  serverResponse: ProcessFlowchartConfigForEditorWithLatestVersionGraphQL
): ValuableProcedureServerData {
  const nodeConfigs =
    serverResponse.flowchartConfigVersion != null && !isInaccessible(serverResponse.flowchartConfigVersion)
      ? serverResponse.flowchartConfigVersion.templateFlowchart?.nodeConfigs
      : [];
  const allNodes = (nodeConfigs ?? [])
    .map((node) =>
      isTaskNodeDetailsGQL(node.nodeDetails) ? node.nodeDetails.processTaskDefinitionVersion : null
    )
    .map((ptdv) =>
      ptdv &&
      !isInaccessible(ptdv) &&
      ptdv.applicationData?.__typename === 'ProcedureMethodDefinitionVersionNode'
        ? ptdv.applicationData
        : null
    )
    .filter((methodNode): methodNode is NonNullable<typeof methodNode> => methodNode != null);

  /**
   * We extract a mapping of step IDs to their associated document version IDs for all steps in the procedure. The
   * DVIDs are not otherwise present on the cid-id map, or elsewhere on the client or in the server response.
   *
   * These DVIDs need to be installed after a save to ensure we're up to date with the server changes.
   */
  const stepIdToDocumentIdAndVersionIdMap: StepIdToDocumentIdAndVersionIdMap = {};
  const allSteps = allNodes.map((methodNode) => methodNode.stepGroups.flatMap((sg) => sg.steps)).flat();
  for (const step of allSteps) {
    if (step.id != null) {
      // These values are merged into the client flowchart inside mergeIdsIntoProcedureStepConfig
      stepIdToDocumentIdAndVersionIdMap[step.id] = {
        // DocumentID is added when a new step is created by the server. Null values suppress updates.
        documentId: step.documentVersion?.document?.id ?? null,
        // Document /version/ ID is updated on every change to a step document.
        documentVersionId: step.documentVersion?.id ?? null,
      };
    }
  }

  /**
   * We extract three maps from parameter settings in a single pass over allNodes:
   *
   * - parameterVersionIdToParameterIdMap: Maps version IDs to parent parameter IDs. A newly created
   *   parameter on the client has a null ID and no CID, so it won't be in the cid-id map. These IDs
   *   need to be installed to correctly reuse parameter versions on the next save.
   *
   * - parameterPlusSchemaFields: REST schema fields for any parameters or materials that were created
   *   or updated during the save.
   *
   * - parameterVersionDescriptionById: Only populated for library-based data parameters, whose
   *   description is resolved on the server from the library definition. The client uses a custom
   *   eval to display it, which doesn't update the domain model, so we need to merge the
   *   server-resolved description back in. Non-library parameters keep the client as source of truth.
   */
  const parameterVersionIdToParameterIdMap: Record<string, string> = {};
  const parameterPlusSchemaFields: Record<string, SchemaField> = {};
  const parameterVersionDescriptionById: Record<string, string> = {};

  const assignParameterId = (versionId: string, parameterId: string): void => {
    assert(
      parameterVersionIdToParameterIdMap[versionId] == null,
      'Duplicate parameter version ID encountered'
    );
    parameterVersionIdToParameterIdMap[versionId] = parameterId;
  };
  const assignSchemaField = (id: string, restValueField: SchemaField): void => {
    assert(parameterPlusSchemaFields[id] == null, 'Duplicate schema field key encountered');
    parameterPlusSchemaFields[id] = restValueField;
  };
  const assignDescription = (versionId: string, description: string): void => {
    assert(parameterVersionDescriptionById[versionId] == null, 'Duplicate parameter version ID encountered');
    parameterVersionDescriptionById[versionId] = description;
  };

  for (const node of allNodes) {
    for (const {version} of node.dataParameterSettings) {
      assignParameterId(version.id, version.procedureDataParameterId);
      assignSchemaField(version.id, version.restValueField);
      if (version.libraryParameterDefinition != null) {
        assignDescription(version.id, version.description);
      }
    }
    for (const {version} of node.materialInputSettings) {
      assignParameterId(version.id, version.procedureMaterialInputId);
      assignSchemaField(version.id, version.restMaterialField);
      assignSchemaField(createIdForAmountField(version.id), version.restAmountField);
    }
    for (const {version} of node.materialOutputSettings) {
      assignParameterId(version.id, version.procedureMaterialOutputId);
      assignSchemaField(version.id, version.restMaterialField);
    }
    for (const {version} of node.equipmentSettings) {
      assignParameterId(version.id, version.procedureEquipmentId);
      assignSchemaField(version.id, version.restEquipmentField);
    }
  }

  /**
   * We also need the parameter usages to reflect any parameter confirmation table rows that were added or
   * removed. Parameter usages are calculated on the server by querying the tables in the step documents.
   */
  const parameterUsagesGQLByMethodId: Record<string, Array<ParameterUsageNodeGraphQL>> = {};
  for (const node of allNodes) {
    parameterUsagesGQLByMethodId[node.id] = node.parameterUsagesExpensive;
  }

  return {
    stepIdToDocumentIdAndVersionIdMap,
    parameterVersionIdToParameterIdMap,
    parameterPlusSchemaFields,
    parameterVersionDescriptionById,
    parameterUsagesGQLByMethodId,
  };
}

export default class ProcedureRootNodeAdapter extends ProcessFlowchartAdapter<
  null,
  null,
  ValuableProcedureServerData
> {
  readonly executionType: ProcessExecutionType = ProcessExecutionType.PROCEDURE;
  readonly typeDisplayName: string = 'procedure';
  readonly editMode = FlowchartNodeEditMode.DOUBLE_CLICK;

  nodeDetailsSidepanelContent = [];

  convertTaskDefinitionVersionGQLToApplicationData(
    taskDefinitionVersionGQL: ProcessTaskDefinitionVersionGraphQL
  ): null {
    return null;
  }

  convertTaskDefinitionVersionSpecToNetworkApplicationData(
    taskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema<null>,
    flowchartConfigId: string
  ): null {
    return null;
  }

  getNodeName(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema,
    isFlowchartOutput?: boolean
  ): string {
    if (isFlowchartOutput === true) {
      return STANDARD_END_NODE_NAME;
    } else {
      return STANDARD_ROOT_NODE_NAME;
    }
  }

  getNodeSubtitle(taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema): string | null {
    return null;
  }

  getNodeIcon(
    taskDefinitionVersion: TaskDefinitionVersionSpec,
    isFlowchartOutput?: boolean
  ): BenchlingIconName {
    if (isFlowchartOutput === true) {
      return STANDARD_END_NODE_ICON;
    } else {
      return STANDARD_ROOT_NODE_ICON;
    }
  }

  createSections(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema,
    nodeDetailsType: ProcessFlowchartNodeDetailsType
  ): Array<TaskSchemaNodeSectionData> {
    return [];
  }

  onCreateNewNode(
    context: NodeCreationContext
  ): Promise<TaskDefinitionVersionSpecWithTaskSchema<null> | null> {
    assert(false, 'Procedure root nodes should not be created');
  }

  getNewNodeDetails(context: NodeCreationContext): {
    nodeName: string;
    nodeSubtitle: string | null;
    nodeIcon: BenchlingIconName | null;
  } {
    assert(false, 'Procedure root nodes should not be created');
  }

  override isDeletable(): boolean {
    return false;
  }

  getSingletonTaskSchemaName(
    rootTaskSchema: ProcessTaskSchemaGraphQL
  ): ProcessSingletonTaskSchemaName | null {
    return null;
  }

  private _getProcedureType(flowchartConfig: FlowchartConfigWithMetadataAndRootTaskSchema): ProcedureType {
    const clientOrigin = clientOriginToProcedureType[flowchartConfig.metadata.rootTaskSchema.clientOrigin];
    assert(clientOrigin != null, 'Unexpected client origin encountered', {clientOrigin});
    return clientOrigin;
  }

  onSaveStart(flowchartConfig: FlowchartConfigWithMetadataAndRootTaskSchema): void {
    const procedureType = this._getProcedureType(flowchartConfig);
    const eventMetadata = composeProcedureTypeMetadata(procedureType);
    logProfilerTimingEvent(PROCEDURE_SAVE_STARTED_EVENT, eventMetadata);

    const documentVersionUpdates = getDocumentVersionUpdatesFromFlowchartConfig(flowchartConfig);
    DocumentActions.notifySaveStart(documentVersionUpdates);
  }

  /**
   * After a successful save, extract valuable data from the server response, which has some properties
   * that are not available elsewhere. Whatever data we extract here will be passed to `onSaveComplete` below.
   *
   * @param serverResponse - The server response containing the latest flowchart config data.
   * @returns An object containing the mapping of step IDs to document version IDs.
   */
  override extractValuableDataFromServerResponse(
    serverResponse: ProcessFlowchartConfigForEditorWithLatestVersionGraphQL
  ): ValuableProcedureServerData {
    return extractValuableProcedureDataFromServerResponse(serverResponse);
  }
  /**
   * Sync client state with the server after save. The backend returns server-generated IDs for
   * nodes, parameters, and sections we created on the client; we install those so the flowchart
   * uses real IDs and ReactFlow handles and edges stay connected. We also
   * refresh step documents whose version changed on the backend without a local edit, so
   * DocumentStore doesn't keep stale version refs and cause a version mismatch on the next save.
   */
  async onSaveComplete(
    persistedFlowchartConfig: FlowchartConfigWithMetadataAndRootTaskSchema,
    additionalPersistedData: ProcessFlowchartAdditionalPersistedData & {
      valuableServerData: ValuableProcedureServerData;
    }
  ): Promise<FlowchartConfigWithMetadataAndRootTaskSchema> {
    const documentVersionUpdates = getDocumentVersionUpdatesFromFlowchartConfig(persistedFlowchartConfig);
    DocumentActions.notifySaveComplete(documentVersionUpdates, additionalPersistedData.cidToIdMap);

    const result = mergeIdsIntoPowProcedureFlowchartConfig(
      persistedFlowchartConfig,
      additionalPersistedData.cidToIdMap,
      additionalPersistedData.valuableServerData
    );

    /**
     * Refresh documents in the DocumentStore for steps whose document version changed on the
     * backend (e.g., from parameter table updates) but weren't edited by the user. Without this,
     * the DocumentStore retains a stale version reference, causing a version mismatch assertion
     * on the next save that includes a document edit.
     */
    const documentIdsToRefresh = this._getDocumentIdsToRefresh(
      persistedFlowchartConfig,
      additionalPersistedData.cidToIdMap,
      additionalPersistedData.valuableServerData
    );

    if (documentIdsToRefresh.length > 0) {
      await Promise.all(documentIdsToRefresh.map((id) => DocumentActions.refresh(id)));
    }

    const procedureType = this._getProcedureType(persistedFlowchartConfig);
    logProfilerTimingEvent(PROCEDURE_SAVE_ENDED_EVENT, composeProcedureTypeMetadata(procedureType));

    return result;
  }

  /**
   * Identify documents that need to be refreshed because the backend created new document versions
   * (e.g., from parameter table regeneration after a setpoint change) that the DocumentStore doesn't
   * know about. Only returns document IDs for steps that:
   *   1. Had no user document edit in this save (no documentVersionUpdate)
   *   2. Received a new document version ID from the server
   *   3. Have their document currently loaded in the DocumentStore
   */
  private _getDocumentIdsToRefresh(
    persistedFlowchartConfig: FlowchartConfigWithMetadataAndRootTaskSchema,
    cidToIdMap: CidIdMap,
    valuableServerData: ValuableProcedureServerData
  ): Array<string> {
    const documentIdsToRefresh = new Set<string>();

    for (const node of persistedFlowchartConfig.staticFlowchart.nodesStaticData) {
      if (!isStaticMethodNode(node)) {
        continue;
      }
      const methodNode = node.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.applicationData;
      for (const stepGroup of methodNode.stepGroups) {
        for (const step of stepGroup.steps) {
          // Skip steps with a user document edit (already handled by notifySaveComplete)
          // or steps without a document
          if (step.documentVersionUpdate != null || step.documentId == null) {
            continue;
          }
          // Skip steps not part of this save (new node added while save was in flight)
          const stepId = cidToIdMap[step.cid];
          if (stepId == null) {
            continue;
          }
          // Skip steps the server didn't return a version for
          const serverData = valuableServerData.stepIdToDocumentIdAndVersionIdMap[String(stepId)];
          if (serverData?.documentVersionId == null) {
            continue;
          }
          // Version changed on the backend and the document is loaded — refresh it
          if (serverData.documentVersionId !== step.fromId && DocumentStore.inStore(step.documentId)) {
            documentIdsToRefresh.add(step.documentId);
          }
        }
      }
    }

    return [...documentIdsToRefresh];
  }

  override getRemoveNodeConfirmationContent(
    params: RemoveNodeConfirmationParams
  ): RemoveNodeConfirmationContent {
    const clientOrigin = params.metadata.rootTaskSchema.clientOrigin;
    const procedureType = clientOriginToProcedureType[clientOrigin];
    assert(procedureType != null, 'Unexpected procedure type encountered');

    const procedureTypeName = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
      stringCase: StringCase.LOWER,
    });
    const methodTypeName = getProcedureUIString(ProceduresStringKeys.METHOD, procedureType, {
      stringCase: StringCase.LOWER,
      plural: true,
    });

    const title = `Remove from ${procedureTypeName}?`;
    const subtitle = params.isMultiNodeRemoval ? (
      `This will permanently remove these ${methodTypeName} from the ${procedureTypeName}. All material mappings to or from them will also be removed.`
    ) : (
      <>
        This will permanently remove <strong>{params.nodeName}</strong> from the {procedureTypeName}. All
        material mappings to or from it will also be removed.
      </>
    );

    return {title, subtitle};
  }
}
